/**
 * Serviço de Compartilhamento Social com Marca D'água
 * Adiciona marca d'água discreta do NutriSync e permite compartilhamento nas redes sociais
 */

/**
 * Adiciona marca d'água discreta à imagem
 */
export async function addWatermark(imageUrl: string): Promise<string> {
  return new Promise((resolve, reject) => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    if (!ctx) {
      reject(new Error('Não foi possível criar contexto do canvas'));
      return;
    }

    const img = new Image();
    img.crossOrigin = 'anonymous';

    img.onload = () => {
      // Configurar tamanho do canvas
      canvas.width = img.width;
      canvas.height = img.height;

      // Desenhar imagem original
      ctx.drawImage(img, 0, 0);

      // Configurar marca d'água
      const watermarkText = 'NutriSync';
      const fontSize = Math.max(img.width * 0.03, 16); // 3% da largura, mínimo 16px
      const padding = Math.max(img.width * 0.02, 10); // 2% da largura, mínimo 10px

      ctx.font = `${fontSize}px Arial, sans-serif`;
      ctx.fillStyle = 'rgba(255, 255, 255, 0.6)'; // Branco semi-transparente
      ctx.strokeStyle = 'rgba(0, 0, 0, 0.3)'; // Contorno escuro semi-transparente
      ctx.lineWidth = 2;
      ctx.textAlign = 'right';
      ctx.textBaseline = 'bottom';

      // Posicionar no canto inferior direito
      const x = img.width - padding;
      const y = img.height - padding;

      // Desenhar contorno
      ctx.strokeText(watermarkText, x, y);
      // Desenhar texto
      ctx.fillText(watermarkText, x, y);

      // Converter para data URL
      const watermarkedImage = canvas.toDataURL('image/png', 0.95);
      resolve(watermarkedImage);
    };

    img.onerror = () => {
      reject(new Error('Erro ao carregar imagem'));
    };

    // Adicionar timestamp para evitar cache
    const imageUrlWithTimestamp = imageUrl.includes('?')
      ? `${imageUrl}&t=${Date.now()}`
      : `${imageUrl}?t=${Date.now()}`;

    img.src = imageUrlWithTimestamp;
  });
}

/**
 * Baixa imagem com marca d'água
 */
export async function downloadImageWithWatermark(imageUrl: string, filename: string = 'nutrisync-post.png'): Promise<void> {
  try {
    const watermarkedImage = await addWatermark(imageUrl);

    // Criar link de download
    const link = document.createElement('a');
    link.href = watermarkedImage;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  } catch (error) {
    console.error('Erro ao baixar imagem:', error);
    throw error;
  }
}

/**
 * Compartilha imagem no Facebook
 */
export function shareOnFacebook(imageUrl: string, text: string = ''): void {
  const url = encodeURIComponent(imageUrl);
  const quote = encodeURIComponent(text || 'Confira minha jornada de saúde no NutriSync!');
  window.open(
    `https://www.facebook.com/sharer/sharer.php?u=${url}&quote=${quote}`,
    '_blank',
    'width=600,height=400'
  );
}

/**
 * Compartilha no Twitter/X
 */
export function shareOnTwitter(imageUrl: string, text: string = ''): void {
  const url = encodeURIComponent(imageUrl);
  const tweet = encodeURIComponent(text || 'Minha jornada de saúde com NutriSync 💪 #NutriSync #Saúde #Nutrição');
  window.open(
    `https://twitter.com/intent/tweet?text=${tweet}&url=${url}`,
    '_blank',
    'width=600,height=400'
  );
}

/**
 * Compartilha no WhatsApp
 */
export function shareOnWhatsApp(imageUrl: string, text: string = ''): void {
  const message = encodeURIComponent(`${text || 'Confira minha jornada de saúde no NutriSync!'}\n\n${imageUrl}`);
  window.open(
    `https://wa.me/?text=${message}`,
    '_blank'
  );
}

/**
 * Compartilha no LinkedIn
 */
export function shareOnLinkedIn(imageUrl: string): void {
  const url = encodeURIComponent(imageUrl);
  window.open(
    `https://www.linkedin.com/sharing/share-offsite/?url=${url}`,
    '_blank',
    'width=600,height=400'
  );
}

/**
 * Copia link da imagem para área de transferência
 */
export async function copyImageLink(imageUrl: string): Promise<void> {
  try {
    await navigator.clipboard.writeText(imageUrl);
  } catch (error) {
    // Fallback para navegadores antigos
    const textArea = document.createElement('textarea');
    textArea.value = imageUrl;
    textArea.style.position = 'fixed';
    textArea.style.left = '-999999px';
    document.body.appendChild(textArea);
    textArea.select();
    document.execCommand('copy');
    document.body.removeChild(textArea);
  }
}

/**
 * Compartilha usando Web Share API (mobile-friendly)
 */
export async function shareNative(imageUrl: string, text: string = '', title: string = 'NutriSync'): Promise<boolean> {
  if (!navigator.share) {
    return false;
  }

  try {
    // Primeiro, adicionar marca d'água
    const watermarkedImage = await addWatermark(imageUrl);

    // Converter data URL para Blob
    const response = await fetch(watermarkedImage);
    const blob = await response.blob();
    const file = new File([blob], 'nutrisync-post.png', { type: 'image/png' });

    await navigator.share({
      title,
      text: text || 'Confira minha jornada de saúde no NutriSync!',
      files: [file]
    });

    return true;
  } catch (error) {
    console.error('Erro ao compartilhar:', error);
    return false;
  }
}



